import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import type {
  FileDiagnostic,
  VerificationResult,
} from '../../models/diagnostics.models';

@Component({
  selector: 'app-diagnostic-file-verification',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './diagnostic-file-verification.component.html',
  styleUrls: ['./diagnostic-file-verification.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosticFileVerificationComponent {
  @Input() files: FileDiagnostic[] = [];

  getRowClass(result: VerificationResult): string {
    switch (result) {
      case 1:
        return 'row-mismatch';
      case 2:
        return 'row-warn';
      case 3:
        return 'row-info';
      default:
        return '';
    }
  }

  getResultBadgeClass(result: VerificationResult): string {
    switch (result) {
      case 0:
        return 'badge-ok';
      case 1:
        return 'badge-mismatch';
      case 2:
        return 'badge-error';
      case 3:
        return 'badge-info';
      default:
        return 'badge-na';
    }
  }

  getResultLabel(result: VerificationResult): string {
    switch (result) {
      case 0:
        return 'OK';
      case 1:
        return 'MISMATCH';
      case 2:
        return 'ERROR';
      case 3:
        return 'INFO';
      default:
        return 'N/A';
    }
  }

  getDotClass(result: VerificationResult): string {
    switch (result) {
      case 0:
        return 'dot-green';
      case 1:
        return 'dot-red';
      case 2:
        return 'dot-yellow';
      case 3:
        return 'dot-blue';
      default:
        return 'dot-grey';
    }
  }

  getExistsLabel(exists: boolean | null): string {
    if (exists === null) return '—';
    return exists ? 'YES' : 'NO';
  }

  getExistsClass(exists: boolean | null): string {
    if (exists === null) return 'exists-na';
    return exists ? 'exists-yes' : 'exists-no';
  }

  getPrimaryExists(file: {
    existsOnSftp: boolean | null;
    existsInObjectStorage: boolean | null;
  }): boolean | null {
    if (file.existsOnSftp !== null) return file.existsOnSftp;
    return file.existsInObjectStorage;
  }

  getPrimarySize(file: {
    sftpSizeBytes: number | null;
    objectStorageSizeBytes: number | null;
  }): number | null {
    if (file.sftpSizeBytes !== null) return file.sftpSizeBytes;
    return file.objectStorageSizeBytes;
  }

  getPrimaryHash(file: {
    sftpSha256: string | null;
    objectStorageSha256: string | null;
  }): string | null {
    const hash = file.sftpSha256 ?? file.objectStorageSha256;
    if (!hash) return null;
    if (hash.length <= 13) return hash;
    return hash.substring(0, 8) + '…' + hash.substring(hash.length - 5);
  }

  trackByFileId(_index: number, file: FileDiagnostic): number {
    return file.fileId;
  }
}
